import { screen_2217_emp_profile } from "../config/2217_emp_profile";
import {
  ComponentConfig,
  ComponentType,
  FlexDirection,
  LayoutTypes,
} from "../services/types/ComponentTypes";
import { isComponentVisible } from "./visibilityUtils";

export const EMPLOYEE_PROFILE_SCREEN_ID = "2217";
export const PROFILE_EDIT_DRAWER_SCREEN_ID = "2217-edit";

export interface ProfileSectionConfig {
  sectionId: string;
  title: string;
  editDrawerId?: string;
  editTitle?: string;
  editConfigId?: string;
  saveActionId?: string;
  editVisibility?: number[];
  enabledForCountries?: string[];
  fields?: ComponentConfig[];
}

const JOB_SAVE_ACTIONS = new Set([
  "save_profile_job_details",
  "save_profile_organization",
  "save_profile_employee_time",
  "save_profile_other_settings",
]);

const ASSETS_SAVE_ACTIONS = new Set(["save_profile_assets"]);

function walkComponentTree(
  components: ComponentConfig[],
  visitor: (comp: ComponentConfig) => boolean | void
): void {
  for (const comp of components) {
    if (visitor(comp)) return;
    const kids = (comp as any).childComponents;
    if (Array.isArray(kids)) walkComponentTree(kids, visitor);
  }
}

/** Walk 2217 profileTabs config and collect all tab sections. */
export function getAllProfileSections(): ProfileSectionConfig[] {
  const sections: ProfileSectionConfig[] = [];
  walkComponentTree(screen_2217_emp_profile as ComponentConfig[], (comp) => {
    if (String(comp.componentType).toLowerCase() !== "profiletabs") return false;
    const tabConfigs = (comp as any).config?.tabConfigs;
    if (!tabConfigs || typeof tabConfigs !== "object") return false;
    for (const tabConfig of Object.values(tabConfigs) as any[]) {
      const tabSections: ProfileSectionConfig[] = tabConfig?.sections || [];
      sections.push(...tabSections);
    }
    return false;
  });
  return sections;
}

export function findProfileSectionByEditDrawerId(
  editDrawerId: string
): ProfileSectionConfig | undefined {
  return getAllProfileSections().find((s) => s.editDrawerId === editDrawerId);
}

export function findProfileSectionBySaveActionId(
  saveActionId: string
): ProfileSectionConfig | undefined {
  return getAllProfileSections().find((s) => s.saveActionId === saveActionId);
}

export function canEditProfileSection(section: ProfileSectionConfig): boolean {
  return isComponentVisible(section.editVisibility);
}

export function filterSectionsByCountry(
  sections: ProfileSectionConfig[],
  countryCode?: string | null
): ProfileSectionConfig[] {
  if (!countryCode) return sections;
  return sections.filter((section) => {
    if (section.enabledForCountries) {
      return section.enabledForCountries.includes(countryCode);
    }
    return true;
  });
}

export function getProfileSaveEndpoint(
  saveActionId: string
): "about" | "job" | "assets" | null {
  if (JOB_SAVE_ACTIONS.has(saveActionId)) return "job";
  if (ASSETS_SAVE_ACTIONS.has(saveActionId)) return "assets";
  if (saveActionId === "save_profile_documents") return null;
  if (saveActionId.startsWith("save_profile_")) return "about";
  return null;
}

/** Build API payload keys from section field definitions + drawer form values. */
export function buildProfileSectionPayload(
  fields: ComponentConfig[] | undefined,
  formData: Record<string, any>
): Record<string, any> {
  const payload: Record<string, any> = {};
  if (!fields?.length) return payload;

  for (const field of fields) {
    const f = field as any;
    const jsonAttr = f.Json_Attribute || f.json_Attribute;
    const bindValue = f.BindValue || f.name;
    if (!jsonAttr) continue;

    let value: unknown =
      bindValue && formData[bindValue] !== undefined
        ? formData[bindValue]
        : formData[jsonAttr];

    if (value === undefined || value === null) continue;
    payload[jsonAttr] = value;
  }

  return payload;
}

/** Copy bind keys from parent profile form into drawer edit form. */
export function pickProfileDrawerFormData(
  fields: ComponentConfig[] | undefined,
  parentFormData: Record<string, any>
): Record<string, any> {
  const out: Record<string, any> = {};
  if (!fields?.length) return out;

  for (const field of fields) {
    const f = field as any;
    const jsonAttr = f.Json_Attribute || f.json_Attribute;
    const bindValue = f.BindValue || f.name;
    const keys = [bindValue, jsonAttr, f.name].filter(Boolean) as string[];
    for (const k of keys) {
      if (parentFormData[k] !== undefined && parentFormData[k] !== null) {
        if (bindValue) out[bindValue] = parentFormData[k];
        if (jsonAttr && jsonAttr !== bindValue) out[jsonAttr] = parentFormData[k];
        break;
      }
    }
    const selectedKey = bindValue ? `${bindValue}_selectedData` : null;
    if (selectedKey && parentFormData[selectedKey]) {
      out[selectedKey] = parentFormData[selectedKey];
    }
  }

  return out;
}

/** Minimal form layout for ComponentRenderer inside the edit drawer. */
export function buildProfileEditDrawerConfig(
  fields: ComponentConfig[],
  saveActionId: string
): ComponentConfig[] {
  const editableFields = fields.map((field) => {
    const f = { ...(field as any) };
    delete f.isReadOnly;
    delete f.disabled;
    return f;
  });

  return [
    {
      componentType: LayoutTypes.FORM,
      Name: "profile_edit_drawer_form",
      childComponents: [
        ...editableFields,
        {
          componentType: LayoutTypes.GROUP,
          style: {
            direction: FlexDirection.ROW,
            justifyContent: "flex-end",
            gap: 8,
            padding: 8,
            marginTop: 12,
          },
          childComponents: [
            {
              componentType: ComponentType.BUTTON,
              name: "Cancel",
              Label: "Cancel",
              onPressAction: "close_profile_edit_drawer",
            },
            {
              componentType: ComponentType.BUTTON,
              name: "Save",
              Label: "Save",
              onPressAction: saveActionId,
              style: {
                backgroundColor: "var(--primary-green)",
                textColor: "#FFFFFF",
              },
            },
          ],
        } as ComponentConfig,
      ],
    } as ComponentConfig,
  ];
}
